/**
 * Shared hierarchical-correlation contract.
 *
 * Every WebSocket sender across the monorepo stamps the subset of IDs it
 * knows onto outbound messages so realtime-api can re-tag the same session
 * (machine -> broadcast -> player_connection) for any inbound message. This
 * module is intentionally free of any Sentry dependency so surfaces that only
 * need ID stamping do not pull the SDK. See `traceAttach.ts` for the
 * Sentry-aware companion that also propagates the active trace.
 */

/** The hierarchical IDs a sender may know about at send time. */
export interface CorrelationIds {
  machineId?: string;
  broadcastId?: string;
}

/**
 * Shape of any message that can carry correlation + trace context. Mirrors the
 * optional fields added to `baseMessageSchema` in `libs/Messaging`.
 */
export interface CorrelatableMessage {
  _machineId?: string;
  _broadcastId?: string;
  _sentryTrace?: string;
  _sentryBaggage?: string;
}

/**
 * Stamp the provided correlation IDs onto a message, mutating in place.
 * Only IDs that are defined are written, so an absent ID is never confused
 * with an empty string downstream. Returns the same message reference.
 */
export function attachCorrelationToMessage<T extends CorrelatableMessage>(
  message: T,
  ids: CorrelationIds,
): T {
  if (ids.machineId !== undefined) {
    message._machineId = ids.machineId;
  }
  if (ids.broadcastId !== undefined) {
    message._broadcastId = ids.broadcastId;
  }
  return message;
}
